import cn from 'clsx';
import type {MouseEvent, ReactNode} from 'react';

import GradientBorderCard from '@/components/GradientBorderCard/GradientBorderCard';
import RemoteIcon from '@/components/RemoteIcon/RemoteIcon';
import type {ESTipType} from '@/consts/experienceScore/esTipTypes';
import {TIP_BORDER_GRADIENT} from '@/consts/gradients';
import type {IconModel} from '@/types/aem';

import {type ESTipWrapper, resolveESTipVariant} from './esTipVariants';
import useTipFillColor from './hooks/useTipFillColor';
import TipCard from './TipCard';

interface ESTipProps {
    type?: ESTipType;
    icon?: IconModel;
    title?: string;
    description?: string;
    btnText?: string;
    className?: string;
    /** Present only when the tip has somewhere to go - the whole card is the button. */
    onClick?: (e: MouseEvent) => void;
}

interface WrapperProps {
    wrapper: ESTipWrapper;
    className: string;
    fillColor: string;
    onClick?: (e: MouseEvent) => void;
    children: ReactNode;
}

const CardWrapper = ({wrapper, className, fillColor, onClick, children}: WrapperProps) => {
    const Tag = onClick ? 'button' : 'div';
    const clickProps = onClick ? ({type: 'button', onClick} as const) : {};
    const interactiveClass = cn(className, onClick && 'cursor-pointer text-left');

    if (wrapper === 'gradient') {
        return (
            <GradientBorderCard
                as={Tag}
                fillColor={fillColor}
                borderGradient={TIP_BORDER_GRADIENT}
                className={interactiveClass}
                {...clickProps}
            >
                {children}
            </GradientBorderCard>
        );
    }

    const borderClass = wrapper === 'flatWarning' ? 'border-gold' : 'border-transparent';

    return (
        <Tag
            className={cn(interactiveClass, 'border-2 bg-light-silver tracking-body dark:bg-orion-2', borderClass)}
            {...clickProps}
        >
            {children}
        </Tag>
    );
};

const ESTip = ({type, icon, className, onClick, ...rest}: ESTipProps) => {
    const fillColor = useTipFillColor();

    if (!rest.title && !rest.description) return null;

    const variant = resolveESTipVariant(type);

    return (
        <CardWrapper
            wrapper={variant.wrapper}
            className={cn('flex w-full flex-col gap-2 rounded-xl p-4', className)}
            fillColor={fillColor}
            onClick={onClick}
        >
            <TipCard
                icon={<RemoteIcon icon={icon} className='size-12 shrink-0' />}
                titleSizeClass={variant.titleSizeClass}
                {...rest}
            />
        </CardWrapper>
    );
};

export default ESTip;
